import {
  type ObjectDefinition,
  categories,
  encodePlayer,
  objects,
  packVec3,
} from "@dust/world/internal";
import IWorldAbi from "@dust/world/out/IWorld.sol/IWorld.abi";
import { resourceToHex } from "@latticexyz/common";
import { isNotNull } from "@latticexyz/common/utils";
import {
  camelToSpaces,
  decodeTransactionError,
  formatPrice,
  getObjectImageUrl,
  objectTypes,
  truncateStr,
} from "@permutation-city/common";
import { TableWithColumns } from "@permutation-city/ui";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDustClient } from "../../hooks/useDustClient";
import {
  cityForceFieldId,
  useForceFieldQuery,
} from "../../queries/useForceFieldQuery";
import { usePlayerPositionQuery } from "../../queries/usePlayerPositionQuery";
import { useAppStore } from "../../store/appStore";
import { useBlueprintQuery } from "./useBlueprintQuery";
import { IncentiveAction, useIncentivesQuery } from "./useIncentivesQuery";
import { useSelectedObjectTypeQuery } from "./useSelectedObjectTypeQuery";

const objectsByObjectType = Object.fromEntries(
  Object.entries(objects).map(([, value]) => [value.id, value]),
) as Record<number, ObjectDefinition>;

export function Blueprint() {
  const { autoSwitch, setAutoSwitch } = useAppStore();
  const [activeObjectTypeId, setActiveObjectTypeId] = useState(0);
  const { data: dustClient } = useDustClient();
  const { data: incentives } = useIncentivesQuery();
  const { data: playerPosition } = usePlayerPositionQuery();
  const { data: selectedObjectType } = useSelectedObjectTypeQuery(autoSwitch);

  const {
    x: chunkX,
    y: chunkY,
    z: chunkZ,
  } = useMemo(() => {
    return {
      x: Math.floor((playerPosition?.x ?? 0) / 16),
      y: Math.floor((playerPosition?.y ?? 0) / 16),
      z: Math.floor((playerPosition?.z ?? 0) / 16),
    };
  }, [playerPosition?.x, playerPosition?.y, playerPosition?.z]);

  const { data: blueprintData, isLoading: isBlueprintLoading } =
    useBlueprintQuery(chunkX, chunkY, chunkZ);
  const { data: forceField, refetch: refetchForceField } = useForceFieldQuery(
    playerPosition?.x ?? 0,
    playerPosition?.y ?? 0,
    playerPosition?.z ?? 0,
  );

  const expandForceField = useMutation({
    mutationFn: async () => {
      if (!dustClient || !blueprintData || blueprintData.length === 0) return;
      if (!forceField || forceField.current.forceField) {
        return;
      }
      if (!forceField.adjacent) {
        throw new Error(
          "No adjacent force field found. Cannot expand force field.",
        );
      }
      console.log(
        "found blueprint, but no force field, expanding force field",
        forceField,
      );
      const userAddress = dustClient.appContext.userAddress;
      const userEntityId = encodePlayer(userAddress);

      const result = await dustClient.provider.request({
        method: "systemCall",
        params: [
          {
            systemId: resourceToHex({
              type: "system",
              namespace: "",
              name: "ForceFieldSystem",
            }),
            abi: IWorldAbi,
            functionName: "addFragment",
            args: [
              userEntityId,
              cityForceFieldId,
              packVec3(forceField.adjacent.fragmentPos),
              packVec3(forceField.current.fragmentPos),
              "0x",
            ],
          },
        ],
      });
      console.log("addFragment result", result);

      const errorMessage = decodeTransactionError(IWorldAbi, result);
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      refetchForceField();
      return result;
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(expandForceField): suppress dependency expandForceField
  // biome-ignore lint/correctness/useExhaustiveDependencies(dustClient): suppress dependency dustClient
  // biome-ignore lint/correctness/useExhaustiveDependencies(blueprintData): suppress dependency blueprintData
  // biome-ignore lint/correctness/useExhaustiveDependencies(forceField): suppress dependency forceField
  useEffect(() => {
    expandForceField.mutate();
  }, [dustClient, blueprintData, forceField, expandForceField.mutate]);

  const setBlueprint = useCallback(async () => {
    if (!dustClient || !blueprintData) return;

    const filteredBlueprintData =
      activeObjectTypeId > 1
        ? blueprintData.filter(({ objectTypeId }) => {
            return activeObjectTypeId === objectTypeId;
          })
        : blueprintData;

    await dustClient.provider.request({
      method: "setBlueprint",
      params: {
        blocks: filteredBlueprintData,
        options:
          activeObjectTypeId === 1
            ? {
                showBlocksToBuild: false,
                showBlocksToMine: true,
              }
            : undefined,
      },
    });
  }, [blueprintData, dustClient, activeObjectTypeId]);

  const blueprintItemsTableData = useMemo(() => {
    if (!blueprintData) return [];

    const uniqueObjectTypes = [
      ...new Set(blueprintData.map(({ objectTypeId }) => objectTypeId)),
    ];

    const blueprintItemsTableData = uniqueObjectTypes
      .map((objectTypeId) => {
        // TODO: improve logic, ts
        let actionType = IncentiveAction.BuildBlueprint;
        // if "air", treat as mineable
        if (objectTypeId === 1) {
          actionType = IncentiveAction.MineBlueprint;
        }

        if (objectTypeId === 1) {
          return null;
        }

        const incentive =
          incentives?.[actionType][objectTypeId] || incentives?.[actionType][0];

        return {
          id: objectTypeId,
          name: {
            value: truncateStr(
              camelToSpaces(objectTypes[objectTypeId]?.name).toUpperCase(),
            ),
            icon: getObjectImageUrl(objectTypeId),
          },
          reward: formatPrice({
            price: incentive?.tokenAmount,
            decimals: 2,
          }),
          reputation: formatPrice({
            price: incentive?.reputationAmount,
            decimals: 2,
          }),
        };
      })
      .filter(isNotNull);

    const mineIncentive = incentives?.[IncentiveAction.MineBlueprint][0];
    const airIncentive = incentives?.[IncentiveAction.MineBlueprint][1];

    return [
      {
        id: -1,
        name: {
          value: "SCAFFOLD (DIRT)",
          icon: getObjectImageUrl(22),
        },
        reward: `-${formatPrice({
          price: incentives?.[IncentiveAction.BuildScaffold][0]?.tokenAmount,
          decimals: 2,
        })}`,
        reputation: formatPrice({
          price:
            incentives?.[IncentiveAction.BuildScaffold][0]?.reputationAmount,
          decimals: 2,
        }),
      },
      {
        id: 1,
        name: {
          value: "MINE",
          icon: getObjectImageUrl(32768),
        },
        reward: formatPrice({
          price: airIncentive?.tokenAmount ?? mineIncentive?.tokenAmount,
          decimals: 2,
        }),
        reputation: formatPrice({
          price:
            airIncentive?.reputationAmount ?? mineIncentive?.reputationAmount,
          decimals: 2,
        }),
      },
      {
        id: 0,
        name: {
          value: "ALL BUILDING BLOCKS",
          icon: getObjectImageUrl(0),
        },
        reward: "",
      },
      ...blueprintItemsTableData,
    ];
  }, [blueprintData, incentives]);

  useEffect(() => {
    setBlueprint();
  }, [setBlueprint]);

  useEffect(() => {
    if (autoSwitch) {
      if (selectedObjectType) {
        const selectedObject = objectsByObjectType[selectedObjectType];
        if (
          selectedObject &&
          categories.Tool.objects.includes(selectedObject.name)
        ) {
          setActiveObjectTypeId(1);
        } else {
          setActiveObjectTypeId(selectedObjectType);
        }
      } else {
        setActiveObjectTypeId(0);
      }
    }
  }, [autoSwitch, selectedObjectType]);

  if (!blueprintData || blueprintData.length === 0) {
    return (
      <div>
        You&apos;re too far from Permutation City. Come to (503, 149, -1585) to
        build the city.
      </div>
    );
  }

  if (
    !forceField ||
    forceField.current.forceField?.forceFieldId !== cityForceFieldId
  ) {
    if (expandForceField.isPending) {
      return <div>Expanding force field...</div>;
    }
    if (expandForceField.isError) {
      return <div>{expandForceField.error.message}</div>;
    }

    if (forceField?.current.forceField?.forceFieldId !== undefined) {
      return (
        <div>
          You cannot build in enemy territory. Destroy the enemy force field
          first.
        </div>
      );
    }
  }

  return (
    <>
      <TableWithColumns
        columns={[
          {
            name: "Name",
            key: "name",
          },
          {
            name: "Pesos",
            key: "reward",
          },
          {
            name: "Rep",
            key: "reputation",
          },
        ]}
        activeRowId={activeObjectTypeId}
        onClick={(item) => {
          setActiveObjectTypeId(Number(item.id));
          setAutoSwitch(false);
        }}
        data={blueprintItemsTableData}
        loading={isBlueprintLoading}
        searchable
      />
    </>
  );
}
